import React, { useEffect, useState } from "react";
import { ScheduleV2 } from "../../../types";
import { useNavigate } from "react-router";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout/slice";
import { setSelectedScheduleForCannotWork } from "../../../redux/features/schedule/ScheduleSlice";
import { releaseOpenShift } from "../../../redux/features/schedule/ScheduleApi";

interface ScheduleActionablesProps {
    schedule: ScheduleV2;
}

const ScheduleActionables: React.FC<ScheduleActionablesProps> = ({ schedule }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [showReleaseModal, setShowReleaseModal] = useState(false);
    const user = useSelector((state: RootState) => state.user.user);

    const requestState = useSelector((state: any) => state.schedule.requestState);
    const isLoading = requestState === "pending";

    useEffect(() => {
        console.log("user", user);
    }, [user]);

    const handleCantWorkClicked = () => {
        dispatch(setTopNavNameTitle("What do you want to do?"));
        dispatch(setSelectedScheduleForCannotWork(schedule));
        navigate("/m-cannotwork-actionables");
    };

    const handleSwapClicked = () => {
        dispatch(setTopNavNameTitle("Trade Shift"));
        dispatch(setSelectedScheduleForCannotWork(schedule));
        navigate("/m-swap-trade");
    };

    const handleReleaseClicked = () => {
        setShowReleaseModal(true);
    };

    const handleConfirmRelease = async () => {
        try {
            await dispatch(releaseOpenShift({ schedule_id: schedule.schedules_id?.toString() || "" })).unwrap();
            // API call successful, navigate back
            window.history.back();
        } catch (error) {
            console.error("Failed to release shift:", error);
            // You might want to show an error message here
        }
    };

    const handleCancelRelease = () => {
        setShowReleaseModal(false);
    };

    const isOwnSchedule = String(user?.os_emp_account_id ?? "") === String(schedule?.emp_details?.emp_id ?? "");
    const canSwap = schedule?.action_button?.can_swap === true;
    const canRelease = schedule?.action_button?.can_release === true;

    // Only show actions for user's own schedule and when at least one action is enabled
    if (!isOwnSchedule || (!canSwap && !canRelease)) {
        return null;
    }

    // Determine button width classes based on how many buttons are present
    const getButtonWidthClass = () => {
        if (canSwap && canRelease) {
            return "w-1/2"; // 50% width when both buttons are present
        }
        return "w-full"; // Full width when only one button is present
    };

    return (
        <>
            <div className="w-full p-4 bg-white border-t border-gray-300 flex space-x-2 fixed bottom-0 left-0 md:w-1/2 md:left-1/2 md:transform md:-translate-x-1/2">
                <>
                    {canSwap && (
                            <button
                                className={`${getButtonWidthClass()} border border-blue-500 text-blue-500 font-semibold py-2 px-4 text-size-md rounded-lg shadow hover:bg-blue-50`}
                                onClick={handleSwapClicked}
                            >
                                Swap
                            </button>
                    )}
                    {canRelease && (
                            <button
                                className={`${getButtonWidthClass()} border border-orange-500 text-orange-500 font-semibold py-2 px-4 text-size-md rounded-lg shadow hover:bg-orange-50 disabled:opacity-50`}
                                onClick={handleReleaseClicked}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Releasing...
                                    </div>
                                ) : (
                                    "Release"
                                )}
                            </button>
                    )}
                </>
            </div>

            {/* Release Confirmation Modal */}
            {showReleaseModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 mx-4 max-w-sm w-full">
                        <div className="text-center">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Release Shift</h3>
                            <p className="text-gray-600 mb-6">
                                Are you sure you want to release this shift? This action cannot be undone.
                            </p>

                            <div className="flex space-x-3">
                                <button
                                    className="flex-1 border border-gray-400 text-gray-700 font-semibold py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
                                    onClick={handleCancelRelease}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="flex-1 border border-orange-500 text-orange-500 font-semibold py-2 px-4 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50"
                                    onClick={handleConfirmRelease}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Releasing...
                                        </div>
                                    ) : (
                                        "Release"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ScheduleActionables;
